import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useParams, useSearchParams, useLocation } from 'react-router-dom';
import styled from '@emotion/styled';
import { motion, AnimatePresence } from 'framer-motion';
import io from 'socket.io-client';
import { MoveUpRight, X, AlertCircle, User, MessageCircle, RefreshCw, DollarSign, Calendar, TrendingUp, Edit3 } from 'lucide-react';
import useAuth from '../../hooks/useAuth';

const ChatContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100vh;
  background: #25253C;
  color: #ffffff;
  font-family: 'Clash Grotesk', sans-serif;
  @media (max-width: 767px) {
    height: auto;
  }
`;

const ChatHeader = styled.div`
  background: #25253C;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  padding: 1rem 2rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  backdrop-filter: blur(10px);

  @media (max-width: 767px) {
    padding: 15px;
  }
`;

const HeaderInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 10px;
`;

const RoomInfo = styled.div`
  display: flex;
  flex-direction: column;
  flex-wrap: wrap;
`;

const RoomTitle = styled.h2`
  font-size: 1.25rem;
  font-weight: 600;
  margin: 0;
  color: #ffffff;
`;

const RoomSubtitle = styled.p`
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.7);
  margin: 0;
`;

const CarInfo = styled.div`
  background: #1E1E35;
  border-radius: 8px;
  padding: 10px;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
`;

const CarIcon = styled.div`
  width: 32px;
  height: 32px;
  background: linear-gradient(45deg, rgb(244, 63, 96), rgb(247, 90, 118));
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.8rem;
  font-weight: 600;
`;

const CarDetails = styled.div`
  display: flex;
  flex-direction: column;
  flex-wrap: wrap;
`;

const CarTitle = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: #fff;
`;

const CarPrice = styled.span`
  font-size: 0.8rem;
  color: rgba(255, 255, 255, 0.8);
`;

const StatusIndicator = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background: ${props => props.isOnline ? 'rgba(34, 197, 94, 0.1)' : 'rgba(239, 68, 68, 0.1)'};
  border: 1px solid ${props => props.isOnline ? 'rgba(34, 197, 94, 0.3)' : 'rgba(239, 68, 68, 0.3)'};
  border-radius: 20px;
  font-size: 14px;
  color: ${props => props.isOnline ? '#22C55E' : '#EF4444'};
`;

const ChatMessages = styled.div`
  flex: 1;
  overflow-y: scroll;
  padding: 2rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;

  @media (max-width: 767px) {
    flex: auto;
    height: 300px;
    padding: 10px;
  }
`;

const MessageBubble = styled(motion.div)`
  max-width: 70%;
  padding: 12px;
  border-radius: 10px;
  line-height: 1.5;
  font-size: 16px;
  position: relative;
  
  ${props => props.isDealer ? `
    background: #1E1E35;
    color: #ffffff;
    align-self: flex-end;
    margin-left: auto;
  ` : `
    background: #313151;
    color: #ffffff;
    align-self: flex-start;
    border: 1px solid rgba(255, 255, 255, 0.1);
  `}
`;

const MessageHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
  font-size: 14px;
  opacity: 0.8;
`;

const MessageTime = styled.span`
  font-size: 14px;
  opacity: 0.7;
`;

const TypingIndicator = styled(motion.div)`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 1rem 1.5rem;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 1rem;
  align-self: flex-start;
  margin-bottom: 1rem;
  
  .dot {
    width: 8px;
    height: 8px;
    background: #F43F60;
    border-radius: 50%;
    animation: typing 1.4s infinite ease-in-out;
  }
  .dot:nth-of-type(1) { animation-delay: 0s; }
  .dot:nth-of-type(2) { animation-delay: 0.2s; }
  .dot:nth-of-type(3) { animation-delay: 0.4s; }
  @keyframes typing {
    0%, 60%, 100% { transform: translateY(0); opacity: 0.4; }
    30% { transform: translateY(-4px); opacity: 1; }
  }
   @media (max-width: 767px) {
    padding: 15px;
  }
`;

const InputContainer = styled.div`
  background: #313151;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  padding: 1.5rem 2rem;

  @media (max-width: 767px) {
    padding: 15px;
  }
`;

const InputWrapper = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 1rem;
  max-width: 100%;
`;

const Input = styled.textarea`
  flex: 1;
  background: #313151;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 12px;
  padding: 12px 16px;
  color: #ffffff;
  font-size: 16px;
  outline: none;
  resize: none;
  min-height: 50px;
  max-height: 50px;
  font-family: inherit;
  line-height: 1.4;
  
  &::placeholder {
    color: rgba(255, 255, 255, 0.5);
  }
  
  &:focus {
    border-color: #F43F60;
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const SendButton = styled.button`
  background: #F43F60;
  width: 50px;
  height: 50px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #ffffff;
  border: none;
  cursor: pointer;
  transition: all 0.2s ease;
  flex-shrink: 0;
  
  &:hover:not(:disabled) {
    background: #E03A56;
    transform: scale(1.05);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ToastContainer = styled.div`
  position: fixed;
  top: 20px;
  right: 20px;
  z-index: 9999;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const Toast = styled(motion.div)`
  background: #EF4444;
  color: #ffffff;
  padding: 16px 20px;
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
  font-family: 'Clash Grotesk', sans-serif;
  font-size: 14px;
  font-weight: 500;
  min-width: 300px;
  max-width: 400px;
  display: flex;
  align-items: center;
  gap: 12px;
  border: 1px solid rgba(255, 255, 255, 0.1);
`;

const ToastIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const ToastContent = styled.div`
  flex: 1;
  line-height: 1.4;
`;

const ToastCloseButton = styled.button`
  background: none;
  border: none;
  color: rgba(255, 255, 255, 0.7);
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  flex-shrink: 0;
  
  &:hover {
    color: #ffffff;
    background: rgba(255, 255, 255, 0.1);
  }
`;

const WelcomeMessage = styled(motion.div)`
  text-align: center;
  padding: 2rem;
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.9rem;

    @media (max-width: 767px) {
    padding: 15px;
  }
`;

const GlobalStyles = styled.div`
  @keyframes spin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }
  
  /* Global select styling for better contrast */
  select {
    background-color: #1a1a1a !important;
    color: #ffffff !important;
  }
  
  select option {
    background-color: #1a1a1a !important;
    color: #ffffff !important;
    padding: 0.5rem !important;
  }
  
  select option:hover {
    background-color: #2a2a2a !important;
  }
  
  select option:checked {
    background-color: #4ECDC4 !important;
    color: #000000 !important;
  }
`;

const ActionButtonsContainer = styled.div`
  display: flex;
  gap: 0.75rem;
  padding: 1rem 2rem;
  background: rgba(255, 255, 255, 0.02);
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  flex-wrap: wrap;

   @media (max-width: 767px) {
    padding: 15px
  }
`;

const ActionButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 8px 12px;
  background: linear-gradient(135deg, #4ECDC4, #44A08D);
  border: none;
  border-radius: 8px;
  color: white;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 0.9rem;
  position: relative;

  
  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
    transform: none;
    background: rgba(255, 255, 255, 0.1);
    color: rgba(255, 255, 255, 0.5);
  }
`;


const ActionButtonWrapper = styled.div`
  position: relative;
  display: inline-block;
`;

const ErrorMessage = styled.div`
  color: #EF4444;
  font-size: 0.8rem;
  margin-top: 0.25rem;
  display: flex;
  align-items: center;
  gap: 0.25rem;
`;

const ErrorIcon = styled.span`
  color: #EF4444;
  font-size: 0.9rem;
`;

const TooltipContainer = styled.div`
  position: absolute;
  bottom: 100%;
  left: 50%;
  transform: translateX(-50%);
  z-index: 1000;
  margin-bottom: 0.5rem;
  opacity: ${props => props.visible ? 1 : 0};
  visibility: ${props => props.visible ? 'visible' : 'hidden'};
  transition: all 0.2s ease;
  pointer-events: none;
`;

const WelOffter = styled.div`
  background: #313151;
  padding: 1rem 2rem;
  font-size: 14px;
  color: #fff;
  @media (max-width: 767px) {
    padding: 15px;
    font-size: 12px;
  }
`;

const TooltipContent = styled.div`
  background: rgba(0, 0, 0, 0.9);
  color: white;
  padding: 0.5rem 0.75rem;
  border-radius: 6px;
  font-size: 0.8rem;
  white-space: nowrap;
  
  &::after {
    content: '';
    position: absolute;
    top: 100%;
    left: 50%;
    transform: translateX(-50%);
    border: 4px solid transparent;
    border-top-color: rgba(0, 0, 0, 0.9);
  }
`;

const ModalOverlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  backdrop-filter: blur(5px);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  padding: 1rem;
`;

const ModalContent = styled(motion.div)`
  background: #25253C;
  border-radius: 1rem;
  width: 100%;
  max-width: 500px;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
  border: 1px solid rgba(255, 255, 255, 0.1);
`;

const ModalHeader = styled.div`
  padding: 1rem 1.5rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  justify-content: space-between;
  align-items: center;
  
  h3 {
    font-size: 1.25rem;
    margin: 0;
    color: #ffffff;
  }
`;

const ModalBody = styled.div`
  padding: 1.5rem;
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
  
  label {
    display: block;
    margin-bottom: 0.5rem;
    color: rgba(255, 255, 255, 0.8);
    font-size: 0.9rem;
    font-weight: 500;
  }
  
  input, select, textarea {
    width: 100%;
    padding: 0.75rem;
    background: #1E1E35;
    border: none;
    border-radius: 8px;
    color: #ffffff;
    font-size: 0.95rem;
    outline: none;
    
    &:focus {
      border-color: #4ECDC4;
    }
    
    &::placeholder {
      color: rgba(255, 255, 255, 0.5);
    }
  }
  
  select {
    bbackground: #1E1E35 !important;
    color: #ffffff !important;
    border: none !important;
    
    &:focus {
      background: #1E1E35 !important;
    }
    
    &:hover {
      background: #1E1E35 !important;
    }
    
    option {
      background: #1a1a1a !important;
      color: #ffffff !important;
      padding: 0.75rem !important;
      border: none !important;
    }
    
    option:hover {
      background: #1E1E35 !important;
    }
    
    option:checked {
      background: #4ECDC4 !important;
      color: #000000 !important;
    }
  }
  
  textarea {
    resize: vertical;
    min-height: 80px;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
  margin-top: 2rem;
`;

const PrimaryButton = styled.button`
  padding: 10px 15px;
  background: linear-gradient(135deg, #4ECDC4, #44A08D);
  border: none;
  border-radius: 8px;
  color: white;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const SecondaryButton = styled.button`
  padding: 10px 15px;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  color: white;
  font-weight: 500;
  cursor: pointer;
  line-height: 1;
  transition: all 0.2s ease;
  
  &:hover {
    background: rgba(255, 255, 255, 0.15);
  }
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: rgba(255, 255, 255, 0.7);
  font-size: 1.5rem;
  cursor: pointer;
  padding: 0.5rem;
  transition: opacity 0.2s;
  line-height: 1;
  
  &:hover {
    opacity: 1;
  }
`;

const OfferMessage = styled(motion.div)`
  background: rgba(78, 205, 196, 0.1);
  border: 1px solid rgba(78, 205, 196, 0.3);
  border-radius: 12px;
  padding: 1rem;
  margin-bottom: 1rem;
  align-self: flex-start;
  
  h4 {
    margin: 0 0 0.5rem 0;
    color: #4ECDC4;
    font-size: 1rem;
  }
  
  p {
    margin: 0.25rem 0;
    color: rgba(255, 255, 255, 0.9);
    font-size: 0.9rem;
  }
`;

const OfferActions = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-top: 0.75rem;
  flex-wrap: wrap;
`;

const OfferActionButton = styled.button`
  padding: 0.5rem 1rem;
  background: rgba(78, 205, 196, 0.2);
  border: 1px solid rgba(78, 205, 196, 0.3);
  border-radius: 6px;
  color: #4ECDC4;
  font-size: 0.8rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: rgba(78, 205, 196, 0.3);
    border-color: rgba(78, 205, 196, 0.5);
  }
`;

const AppointmentMessage = styled(motion.div)`
  background: rgba(34, 197, 94, 0.1);
  border: 1px solid rgba(34, 197, 94, 0.3);
  border-radius: 12px;
  padding: 1rem;
  margin-bottom: 1rem;
  align-self: flex-start;
  
  h4 {
    margin: 0 0 0.5rem 0;
    color: #22C55E;
    font-size: 1rem;
  }
  
  p {
    margin: 0.25rem 0;
    color: rgba(255, 255, 255, 0.9);
    font-size: 0.9rem;
  }
`;

const DealerChat = () => {
  const { roomId } = useParams();
  const location = useLocation();
  
  // Get data from navigation state if available
  const { 
    user: chatUser, 
    vehicle: chatVehicle, 
    offer: chatOffer, 
    roomInfo: chatRoomInfo,
    chatStats: chatStats 
  } = location.state || {};
  
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [socket, setSocket] = useState(null);
  const [isTyping, setIsTyping] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [customerConnected, setCustomerConnected] = useState(false);
  const socketRef = useRef(null);
  const isConnectingRef = useRef(false);
  const [toasts, setToasts] = useState([]);
  const [loadingMessages, setLoadingMessages] = useState(true);
  const [showCreateOfferModal, setShowCreateOfferModal] = useState(false);
  const [showUpdateOfferModal, setShowUpdateOfferModal] = useState(false);
  const [showCounterOfferModal, setShowCounterOfferModal] = useState(false);
  const [showAppointmentModal, setShowAppointmentModal] = useState(false);
  const [loadingAction, setLoadingAction] = useState(false);
  const [offers, setOffers] = useState([]);
  const [hoveredButton, setHoveredButton] = useState(null);
  const [formErrors, setFormErrors] = useState({});
  const [currentOfferStatus, setCurrentOfferStatus] = useState(null);
  const messagesEndRef = useRef(null);
  const connectionStatusRef = useRef(null);
  const { dealer } = useAuth();

  // Get the single active offer we're working with (always the latest offer, regardless of status)
  const getActiveOffer = useCallback(() => {
    // Get all offers (chatOffer + offers array)
    const allOffers = [];
    
    // Add chatOffer if it exists
    if (chatOffer) {
      allOffers.push({
        ...chatOffer,
        status: currentOfferStatus || chatOffer.status,
        isAI: true,
        createdAt: chatOffer.createdAt || '1970-01-01T00:00:00.000Z' // Use a fixed fallback instead of new Date()
      });
    }
    
    // Add offers from offers array
    if (offers.length > 0) {
      offers.forEach(offer => {
        allOffers.push({
          ...offer,
          isAI: false,
          createdAt: offer.createdAt || '1970-01-01T00:00:00.000Z' // Use a fixed fallback instead of new Date()
        });
      });
    }
    
    // Sort by creation date and return the latest offer (regardless of status)
    if (allOffers.length > 0) {
      const sortedOffers = allOffers.sort((a, b) => 
        new Date(b.createdAt) - new Date(a.createdAt)
      );
      
      console.log('All offers:', allOffers.map(o => ({ 
        id: o.id, 
        amount: o.amount, 
        status: o.status, 
        createdAt: o.createdAt, 
        isAI: o.isAI,
        timestamp: new Date(o.createdAt).getTime()
      })));
      console.log('Selected active offer (latest):', sortedOffers[0]);
      console.log('Current time:', new Date().toISOString());
      
      return sortedOffers[0];
    }
    
    console.log('No offers found');
    return null;
  }, [chatOffer, offers, currentOfferStatus]);

  const activeOffer = getActiveOffer();
  const hasActiveOffer = !!activeOffer;
  const isOfferAccepted = activeOffer?.status === 'accepted';
  const isOfferRejected = activeOffer?.status === 'rejected';
  const isOfferCountered = activeOffer?.status === 'countered';
  const isOfferPending = activeOffer?.status === 'pending' || activeOffer?.status === 'sent' || activeOffer?.status === 'active';

  // We only work with one active offer, so no need for complex filtering

  // Form validation functions
  const validateOfferForm = (formData) => {
    const errors = {};
    
    if (!formData.amount || formData.amount.trim() === '') {
      errors.amount = 'Offer amount is required';
    } else {
      const amount = parseFloat(formData.amount);
      if (isNaN(amount)) {
        errors.amount = 'Please enter a valid number';
      } else if (amount <= 0) {
        errors.amount = 'Offer amount must be greater than 0';
      } else if (amount > 1000000) {
        errors.amount = 'Offer amount cannot exceed $1,000,000';
      }
    }
    
    return errors;
  };

  const validateCounterOfferForm = (formData) => {
    const errors = {};
    
    if (!formData.amount || formData.amount.trim() === '') {
      errors.amount = 'Counter offer amount is required';
    } else {
      const amount = parseFloat(formData.amount);
      if (isNaN(amount)) {
        errors.amount = 'Please enter a valid number';
      } else if (amount <= 0) {
        errors.amount = 'Counter offer amount must be greater than 0';
      } else if (amount > 1000000) {
        errors.amount = 'Counter offer amount cannot exceed $1,000,000';
      }
    }
    
    return errors;
  };

  // Business logic for button states - working with latest offer (regardless of status)
  const getButtonStates = () => {
    const baseDisabled = !isConnected;
    
    return {
      createOffer: {
        disabled: baseDisabled || (hasActiveOffer && !isOfferAccepted && !isOfferRejected && !isOfferCountered),
        tooltip: !isConnected 
          ? 'Not connected to server'
          : hasActiveOffer && !isOfferAccepted && !isOfferRejected && !isOfferCountered
          ? 'Cannot create new offer while there is an active offer. Use "Update Offer" or "Counter Offer" instead.'
          : hasActiveOffer && isOfferAccepted
          ? 'Create a new offer (current offer is accepted)'
          : 'Create a new offer'
      },
      updateOffer: {
        disabled: baseDisabled || !hasActiveOffer || isOfferRejected,
        tooltip: !isConnected 
          ? 'Not connected to server'
          : !hasActiveOffer
          ? 'No active offer to update'
          : isOfferRejected 
          ? 'Cannot update rejected offer'
          : activeOffer?.isAI
          ? 'Update the AI offer status'
          : 'Update offer status'
      },
      counterOffer: {
        disabled: baseDisabled || !hasActiveOffer || !isOfferPending,
        tooltip: !isConnected 
          ? 'Not connected to server'
          : !hasActiveOffer
          ? 'No active offer to counter'
          : !isOfferPending
          ? 'Can only counter pending/active offers'
          : activeOffer?.isAI
          ? 'Create a counter offer based on the AI offer'
          : 'Create a counter offer'
      },
      scheduleAppointment: {
        disabled: baseDisabled || !isOfferAccepted,
        tooltip: !isConnected 
          ? 'Not connected to server'
          : !isOfferAccepted 
          ? 'Can only schedule appointment after offer acceptance'
          : 'Schedule an appointment'
      }
    };
  };

  const buttonStates = getButtonStates();

  // Form states
  const [offerForm, setOfferForm] = useState({
    amount: '',
    status: 'pending'
  });
  
  const [updateOfferForm, setUpdateOfferForm] = useState({
    status: 'pending'
  });
  
  const [counterOfferForm, setCounterOfferForm] = useState({
    amount: ''
  });
  
  const [appointmentForm, setAppointmentForm] = useState({
    date: '',
    time: '',
    type: 'test_drive',
    notes: ''
  });

  // Toast functions
  const showToast = useCallback((message, type = 'error') => {
    const id = Date.now() + Math.random();
    const newToast = {
      id,
      message,
      type,
      timestamp: Date.now()
    };
    
    setToasts(prev => [...prev, newToast]);
    
    // Auto remove toast after 5 seconds
    setTimeout(() => {
      removeToast(id);
    }, 5000);
  }, []);

  const removeToast = useCallback((id) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  }, []);

  const scrollToBottom = () => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }
  };

  useEffect(() => {
    if (messages.length > 0) {
      scrollToBottom();
    }
  }, [messages.length]);

  const handleInputChange = useCallback((e) => {
    setInput(e.target.value);
  }, []);

  const handleSend = (e) => {
    e.preventDefault();
    if (!input.trim()) return;
    
    if (socket && isConnected) {
      socket.emit('chatMessageDealer', {
        message: input,
        roomId: roomId,
        userId: chatUser?.id
      });
      
      // Add message to local state immediately for better UX
      const newMessage = {
        type: 'dealer',
        userId: dealer?.id,
        userName: `${dealer?.first_name} ${dealer?.last_name}`,
        content: input,
        timestamp: new Date()
      };
      
      setMessages(prev => [...prev, newMessage]);
      setInput('');
    } else {
      showToast('Connection lost. Please refresh the page.', 'error');
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend(e);
    }
  };

  const updateDealerConnectionStatus = useCallback(async (connected) => {
    // Prevent duplicate calls with the same status
    if (connectionStatusRef.current === connected) {
      return;
    }

    try {
      const token = sessionStorage.getItem('dealerToken') || localStorage.getItem('dealerToken');
      if (!token) {
        console.warn('No dealer token found, skipping connection status update');
        return;
      }

      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/messages/dealer/room/${roomId}/dealer-connection`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ connected })
      });

      if (!response.ok) {
        throw new Error(`Failed to update connection status: ${response.status}`);
      }

      // Update the ref to track current status
      connectionStatusRef.current = connected;
      console.log(`Dealer connection status updated to: ${connected}`);
    } catch (error) {
      console.error('Error updating dealer connection status:', error);
      // Don't retry on error to prevent infinite loops
    }
  }, [roomId]);

  const loadMessageHistory = async () => {
    try {
      setLoadingMessages(true);
      const token = sessionStorage.getItem('dealerToken') || localStorage.getItem('dealerToken');
      
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/messages/dealer/room/${roomId}/messages?limit=100`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to load message history');
      }

      const data = await response.json();
      setMessages(data.data.messages);
    } catch (error) {
      console.error('Error loading message history:', error);
      showToast('Failed to load message history', 'error');
    } finally {
      setLoadingMessages(false);
    }
  };

  // Load message history when component mounts
  useEffect(() => {
    if (roomId && dealer) {
      loadMessageHistory();
    }
  }, [roomId, dealer]);

  // Dealer system actions
  const createOffer = useCallback(() => {
    if (!chatVehicle?.id) return;
    
    // Validate form
    const errors = validateOfferForm(offerForm);
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }
    
    // Clear any previous errors
    setFormErrors({});
    setLoadingAction(true);
    
    socket.emit('dealerSystemAction', {
      action: {
        type: 'insert_offer',
        data: {
          offer: {
            car_id: chatVehicle.id,
            amount: parseFloat(offerForm.amount),
            status: offerForm.status,
            createdAt: new Date(Date.now() + 1000).toISOString() // Add 1 second to ensure it's the latest
          }
        }
      },
      roomId: roomId,
      userId: chatUser?.id
    });
  }, [socket, offerForm, chatVehicle, roomId, chatUser]);

  const updateOfferStatus = useCallback((newStatus) => {
    if (!activeOffer?.id) return;
    
    setLoadingAction(true);
    socket.emit('dealerSystemAction', {
      action: {
        type: 'update_offer_status',
        data: {
          offer_id: activeOffer.id,
          status: newStatus
        }
      },
      roomId: roomId,
      userId: chatUser?.id
    });
  }, [socket, activeOffer, roomId, chatUser]);

  const createCounterOffer = useCallback(() => {
    if (!chatVehicle?.id || !activeOffer?.id) return;
    
    // Validate form
    const errors = validateCounterOfferForm(counterOfferForm);
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }
    
    // Clear any previous errors
    setFormErrors({});
    setLoadingAction(true);
    
    // First, update the active offer status to 'countered'
    socket.emit('dealerSystemAction', {
      action: {
        type: 'update_offer_status',
        data: {
          offer_id: activeOffer.id,
          status: 'countered'
        }
      },
      roomId: roomId,
      userId: chatUser?.id
    });
    
    // Then create the counter offer
    socket.emit('dealerSystemAction', {
      action: {
        type: 'create_counter_offer',
        data: {
          offer: {
            car_id: chatVehicle.id,
            amount: parseFloat(counterOfferForm.amount),
            status: 'pending',
            original_offer_id: activeOffer.id,
            createdAt: new Date(Date.now() + 1000).toISOString() // Add 1 second to ensure it's the latest
          }
        }
      },
      roomId: roomId,
      userId: chatUser?.id
    });
  }, [socket, counterOfferForm, chatVehicle, activeOffer, roomId, chatUser]);

  const scheduleAppointment = useCallback(() => {    
    setLoadingAction(true);
    socket.emit('dealerSystemAction', {
      action: {
        type: 'create_appointment',
       
      },
      roomId: roomId,
      userId: chatUser?.id
    });
    showToast('Appointment request sent!', 'success');

  }, [socket, appointmentForm, roomId, chatUser]);


  // Socket connection and event handling
  useEffect(() => {
    if (!roomId || !dealer) return;

    // Prevent multiple socket connections
    if (socketRef.current || isConnectingRef.current) {
      console.log('Socket already exists or connecting, skipping');
      return;
    }

    isConnectingRef.current = true;

    console.log(socketRef.current)
    console.log('Connecting to socket');
    // Configure Socket.IO to prevent excessive polling and multiple connections
    const newSocket = io(process.env.REACT_APP_API_URL, {
      transports: ['websocket'], // Only use websocket, no polling
      upgrade: false, // Disable upgrade to prevent transport switching
      rememberUpgrade: false,
      timeout: 10000,
      forceNew: true,
      reconnection: false, // Disable automatic reconnection to prevent loops
      autoConnect: true,
      multiplex: false,
      pingTimeout: 60000, // Increase ping timeout
      pingInterval: 60000, // Increase ping interval to reduce frequency
      closeOnBeforeunload: true,
      withCredentials: false
    });
    
    socketRef.current = newSocket;
    setSocket(newSocket);

    // Authenticate dealer
    const token = sessionStorage.getItem('dealerToken');
    if (token) {
      newSocket.emit('dealerAuthenticate', token);
    }

    newSocket.on('authenticated', (data) => {
      console.log('Dealer authenticated:', data);
      setIsConnected(true);
      
      // Join the dealer room
      newSocket.emit('joinDealerRoom', roomId);
      
      // Update dealer connection status
      updateDealerConnectionStatus(true);
    });

    newSocket.on('chatMessage', (message) => {
      console.log('Received message:', message);
      setMessages(prev => [...prev, message]);
      setIsTyping(false);
    });

    // System action result listeners
    newSocket.on('offer', (result) => {
      console.log('Offer created:', result);
      setLoadingAction(false);
      setShowCreateOfferModal(false);
      setShowCounterOfferModal(false);
      setOfferForm({ amount: '', status: 'pending' });
      
      // Add offer message to chat
      const newMessage = {
        type: 'system',
        content: `New offer created: $${result.offer.amount} for ${result.vehicle?.year} ${result.vehicle?.make} ${result.vehicle?.model}`,
        timestamp: new Date(),
        offer: result.offer,
        vehicle: result.vehicle
      };
      
      setMessages(prev => [...prev, newMessage]);
      
      // Add the new offer to the offers array with current timestamp (this will make it the latest active offer)
      // Use a slightly future timestamp to ensure it's always the most recent
      const newOffer = {
        ...result.offer,
        createdAt: new Date(Date.now() + 1000).toISOString(), // Add 1 second to ensure it's the latest
        isAI: false
      };
      
      setOffers(prev => [...prev, newOffer]);
      
      // Clear any existing chatOffer status to ensure new offer becomes active
      setCurrentOfferStatus(null);
      
      console.log('New offer added:', newOffer);
      showToast('Offer created successfully!', 'success');
    });

    newSocket.on('offer_updated', (result) => {
      console.log('Offer updated:', result);
      setLoadingAction(false);
      setShowUpdateOfferModal(false);
      setUpdateOfferForm({ status: 'pending' });
      
      // Update offers list
      setOffers(prev => prev.map(offer => 
        offer.id === result.offer.id ? result.offer : offer
      ));
      
      // Update local offer status if it's the active offer being updated
      if (activeOffer && activeOffer.id === result.offer.id) {
        setCurrentOfferStatus(result.offer.status);
        console.log('Updated active offer status to:', result.offer.status);
      }
      
      // Add update message to chat
      const newMessage = {
        type: 'system',
        content: `Offer status updated to: ${result.offer.status}`,
        timestamp: new Date(),
        offer: result.offer
      };
      
      setMessages(prev => [...prev, newMessage]);
      showToast('Offer updated successfully!', 'success');
    });

    newSocket.on('appointment_created', (result) => {
      console.log('Appointment created:', result);
      setLoadingAction(false);
      setShowAppointmentModal(false);
      setAppointmentForm({ date: '', time: '', type: 'test_drive', notes: '' });
      
      // Add appointment message to chat
      const newMessage = {
        type: 'system',
        content: `Appointment scheduled: ${result.appointment.type} on ${result.appointment.date} at ${result.appointment.time}`,
        timestamp: new Date(),
        appointment: result.appointment
      };
      
      setMessages(prev => [...prev, newMessage]);
      showToast('Appointment scheduled successfully!', 'success');
    });

    newSocket.on('user_typing', () => {
      setIsTyping(true);
    });

    newSocket.on('user_stopped_typing', () => {
      setIsTyping(false);
    });

    newSocket.on('error', (data) => {
      console.error('Socket error:', data);
      setLoadingAction(false);
      showToast(data.message || 'An error occurred', 'error');
    });

    newSocket.on('disconnect', (reason) => {
      console.log('Socket disconnected:', reason);
      setIsConnected(false);
      
      // Only show toast for unexpected disconnections
      if (reason !== 'io client disconnect') {
        showToast('Connection lost. Please refresh the page.', 'error');
      }
    });

    newSocket.on('connect', () => {
      console.log('Socket connected');
      setIsConnected(true);
      isConnectingRef.current = false;
    });

    newSocket.on('connect_error', (error) => {
      console.error('Socket connection error:', error);
      setIsConnected(false);
      isConnectingRef.current = false;
      showToast('Failed to connect. Please check your connection.', 'error');
    });

    return () => {
      // Update dealer connection status to disconnected
      updateDealerConnectionStatus(false);
      
      // Clean up socket connection
      if (socketRef.current) {
        console.log('Cleaning up socket connection');
        socketRef.current.disconnect();
        socketRef.current = null;
      }
      
      // Reset states
      isConnectingRef.current = false;
      setSocket(null);
      setIsConnected(false);
    };
  }, [roomId, dealer]);

  // Auto-resize textarea
  const textareaRef = useRef(null);
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = Math.min(textareaRef.current.scrollHeight, 120) + 'px';
    }
  }, [input]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      // Clean up socket connection
      if (socketRef.current) {
        console.log('Component unmounting, cleaning up socket');
        socketRef.current.disconnect();
        socketRef.current = null;
      }
      
      // Reset all refs
      isConnectingRef.current = false;
      connectionStatusRef.current = null;
    };
  }, []);

  const formatTime = (timestamp) => {
    return new Date(timestamp).toLocaleTimeString([], { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  const formatDate = (timestamp) => {
    const date = new Date(timestamp);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === today.toDateString()) {
      return 'Today';
    } else if (date.toDateString() === yesterday.toDateString()) {
      return 'Yesterday';
    } else {
      return date.toLocaleDateString();
    }
  };

  const renderMessage = (message, index) => {
    if (message.type === 'system') {
      if (message.offer) {
        return (
          <OfferMessage
            key={index}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
          >
            <h4>New Offer Created</h4>
            <p>Vehicle: {message.vehicle?.year} {message.vehicle?.make} {message.vehicle?.model}</p>
            <p>Amount: ${message.offer.amount?.toLocaleString()}</p>
            <p>Status: {message.offer.status}</p>
            {/* <OfferActions>
              <OfferActionButton 
                onClick={() => updateOfferStatus(message.offer.id, 'accepted')}
                disabled={loadingAction}
              >
                Accept
              </OfferActionButton>
              <OfferActionButton 
                onClick={() => updateOfferStatus(message.offer.id, 'rejected')}
                disabled={loadingAction}
              >
                Reject
              </OfferActionButton>
              <OfferActionButton 
                onClick={() => updateOfferStatus(message.offer.id, 'countered')}
                disabled={loadingAction}
              >
                Counter
              </OfferActionButton>
            </OfferActions> */}
          </OfferMessage>
        );
      } else if (message.appointment) {
        return (
          <AppointmentMessage
            key={index}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
          >
            <h4>Appointment Scheduled</h4>
            <p>Type: {message.appointment.type}</p>
            <p>Date: {message.appointment.date}</p>
            <p>Time: {message.appointment.time}</p>
            {message.appointment.notes && <p>Notes: {message.appointment.notes}</p>}
          </AppointmentMessage>
        );
      }
      return (
        <MessageBubble
          key={index}
          isDealer={false}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          transition={{ duration: 0.3 }}
        >
          <div style={{ whiteSpace: 'pre-wrap' }}>{message.content}</div>
        </MessageBubble>
      );
    }

    return (
      <MessageBubble
        key={index}
        isDealer={message.type === 'dealer'}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -20 }}
        transition={{ duration: 0.3 }}
      >
        <MessageHeader>
          {message.type === 'dealer' ? (
            <>
              <User size={14} />
              <span>You</span>
            </>
          ) : (
            <>
              <User size={14} />
              <span>{message.userName || 'Customer'}</span>
            </>
          )}
          <MessageTime>{formatTime(message.timestamp)}</MessageTime>
        </MessageHeader>
        <div style={{ whiteSpace: 'pre-wrap' }}>{message.content}</div>
      </MessageBubble>
    );
  };

  if (!dealer) {
    return (
      <ChatContainer>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
          <p>Please log in to access the dealer chat.</p>
        </div>
      </ChatContainer>
    );
  }

  return (
    <>
      <GlobalStyles />
      {/* Toast Container */}
      <ToastContainer>
        <AnimatePresence>
          {toasts.map((toast) => (
            <Toast
              key={toast.id}
              initial={{ opacity: 0, x: 300, scale: 0.8 }}
              animate={{ opacity: 1, x: 0, scale: 1 }}
              exit={{ opacity: 0, x: 300, scale: 0.8 }}
              transition={{ duration: 0.3, ease: "easeOut" }}
            >
              <ToastIcon>
                <AlertCircle size={20} />
              </ToastIcon>
              <ToastContent>
                {toast.message}
              </ToastContent>
              <ToastCloseButton onClick={() => removeToast(toast.id)}>
                <X size={16} />
              </ToastCloseButton>
            </Toast>
          ))}
        </AnimatePresence>
      </ToastContainer>

      <ChatContainer>
        {/* Header */}
        <ChatHeader>
          <HeaderInfo>
            <div>
              <MessageCircle size={24} color="#F43F60" />
            </div>
            <RoomInfo>
              <RoomTitle>{chatUser?.name || 'Customer Chat'}</RoomTitle>
              <RoomSubtitle>
                Room: {roomId} • Created: {chatRoomInfo?.createdAt ? new Date(chatRoomInfo.createdAt).toLocaleDateString() : 'Unknown'}
              </RoomSubtitle>
            </RoomInfo>
            <button
              onClick={loadMessageHistory}
              disabled={loadingMessages}
              style={{
                background: 'rgba(244, 63, 96, 0.1)',
                border: '1px solid rgba(244, 63, 96, 0.3)',
                color: '#F43F60',
                padding: '0.5rem',
                borderRadius: '8px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.2s ease'
              }}
              title="Refresh messages"
            >
              <RefreshCw size={16} style={{ 
                animation: loadingMessages ? 'spin 1s linear infinite' : 'none' 
              }} />
            </button>
          </HeaderInfo>
          
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
            {chatVehicle && (
              <CarInfo>
                <CarIcon>🚗</CarIcon>
                <CarDetails>
                  <CarTitle>
                    {chatVehicle.year} {chatVehicle.make} {chatVehicle.model}
                  </CarTitle>
                  <CarPrice>
                    ${parseInt(chatVehicle?.price || chatRoomInfo?.price || 0).toLocaleString()}
                  </CarPrice>
                </CarDetails>
              </CarInfo>
            )}
            {activeOffer && (
              <CarInfo style={{ 
                background: activeOffer.isAI ? 'rgba(78, 205, 196, 0.1)' : 'rgba(34, 197, 94, 0.1)', 
                borderColor: activeOffer.isAI ? 'rgba(78, 205, 196, 0.3)' : 'rgba(34, 197, 94, 0.3)' 
              }}>
                <CarIcon style={{ 
                  background: activeOffer.isAI ? 'linear-gradient(45deg, #4ECDC4, #44A08D)' : 'linear-gradient(45deg, #22C55E, #16A34A)' 
                }}>
                  {activeOffer.isAI ? '🤖' : '💰'}
                </CarIcon>
                <CarDetails>
                  <CarTitle style={{ 
                    color: activeOffer.isAI ? '#4ECDC4' : '#22C55E' 
                  }}>
                    {activeOffer.isAI ? 'AI Offer' : 'Offer'}: ${parseInt(activeOffer.amount || 0).toLocaleString()}
                  </CarTitle>
                  <CarPrice>
                    Status: {activeOffer.status}
                  </CarPrice>
                </CarDetails>
              </CarInfo>
            )}
            
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <StatusIndicator isOnline={isConnected}>
                <div style={{ 
                  width: '8px', 
                  height: '8px', 
                  borderRadius: '50%', 
                  background: isConnected ? '#22C55E' : '#EF4444' 
                }} />
                Dealer: {isConnected ? 'Online' : 'Offline'}
              </StatusIndicator>
              <StatusIndicator isOnline={chatRoomInfo?.customerConnected}>
                <div style={{ 
                  width: '8px', 
                  height: '8px', 
                  borderRadius: '50%', 
                  background: chatRoomInfo?.customerConnected ? '#22C55E' : '#EF4444' 
                }} />
                Customer: {chatRoomInfo?.customerConnected ? 'Online' : 'Offline'}
              </StatusIndicator>
            </div>
          </div>
        </ChatHeader>

        {/* Action Buttons */}
        <ActionButtonsContainer>
          <ActionButtonWrapper
            onMouseEnter={() => setHoveredButton('createOffer')}
            onMouseLeave={() => setHoveredButton(null)}
          >
            <ActionButton
              onClick={() => setShowCreateOfferModal(true)}
              disabled={buttonStates.createOffer.disabled}
            >
              <DollarSign size={16} />
              Create Offer
            </ActionButton>
            <TooltipContainer visible={hoveredButton === 'createOffer'}>
              <TooltipContent>{buttonStates.createOffer.tooltip}</TooltipContent>
            </TooltipContainer>
          </ActionButtonWrapper>
          
          <ActionButtonWrapper
            onMouseEnter={() => setHoveredButton('updateOffer')}
            onMouseLeave={() => setHoveredButton(null)}
          >
            <ActionButton
              onClick={() => setShowUpdateOfferModal(true)}
              disabled={buttonStates.updateOffer.disabled}
            >
              <Edit3 size={16} />
              Update Offer
            </ActionButton>
            <TooltipContainer visible={hoveredButton === 'updateOffer'}>
              <TooltipContent>{buttonStates.updateOffer.tooltip}</TooltipContent>
            </TooltipContainer>
          </ActionButtonWrapper>
          
          <ActionButtonWrapper
            onMouseEnter={() => setHoveredButton('counterOffer')}
            onMouseLeave={() => setHoveredButton(null)}
          >
            <ActionButton
              onClick={() => setShowCounterOfferModal(true)}
              disabled={buttonStates.counterOffer.disabled}
            >
              <TrendingUp size={16} />
              Counter Offer
            </ActionButton>
            <TooltipContainer visible={hoveredButton === 'counterOffer'}>
              <TooltipContent>{buttonStates.counterOffer.tooltip}</TooltipContent>
            </TooltipContainer>
          </ActionButtonWrapper>
          
          <ActionButtonWrapper
            onMouseEnter={() => setHoveredButton('scheduleAppointment')}
            onMouseLeave={() => setHoveredButton(null)}
          >
            <ActionButton
              onClick={() => scheduleAppointment()}
              disabled={buttonStates.scheduleAppointment.disabled}
            >
              <Calendar size={16} />
              Schedule Appointment
            </ActionButton>
            <TooltipContainer visible={hoveredButton === 'scheduleAppointment'}>
              <TooltipContent>{buttonStates.scheduleAppointment.tooltip}</TooltipContent>
            </TooltipContainer>
          </ActionButtonWrapper>
        </ActionButtonsContainer>

        {/* Debug Info - Remove in production */}
        {process.env.NODE_ENV === 'development' && (
          <WelOffter>
            <strong>Debug Info:</strong> 
            Active Offer: {activeOffer ? `${activeOffer.status} ($${activeOffer.amount}) ${activeOffer.isAI ? '[AI]' : '[Manual]'} - ${activeOffer.createdAt ? new Date(activeOffer.createdAt).toLocaleTimeString() : 'No time'}` : 'None'} | 
            Total Offers: {offers.length} | 
            ChatOffer: {chatOffer ? 'Yes' : 'No'} | 
            Accepted: {isOfferAccepted ? 'Yes' : 'No'} | 
            Rejected: {isOfferRejected ? 'Yes' : 'No'} | 
            Countered: {isOfferCountered ? 'Yes' : 'No'} | 
            Pending: {isOfferPending ? 'Yes' : 'No'}
           </WelOffter>
        )}

        {/* Messages */}
        <ChatMessages>
          {loadingMessages ? (
            <WelcomeMessage
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <p>Loading message history...</p>
            </WelcomeMessage>
          ) : messages.length === 0 ? (
            <WelcomeMessage
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <p>Welcome to the chat! Start a conversation with your customer.</p>
            </WelcomeMessage>
          ) : (
            <AnimatePresence>
              {messages.map((message, index) => renderMessage(message, index))}
              
              {isTyping && (
                <TypingIndicator
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.3 }}
                >
                  <div className="dot" />
                  <div className="dot" />
                  <div className="dot" />
                  <span style={{ fontSize: '0.8rem', opacity: 0.7 }}>Customer is typing...</span>
                </TypingIndicator>
              )}
            </AnimatePresence>
          )}
          <div ref={messagesEndRef} />
        </ChatMessages>

        {/* Input */}
        <InputContainer>
          <form onSubmit={handleSend}>
            <InputWrapper>
              <Input
                ref={textareaRef}
                value={input}
                onChange={handleInputChange}
                onKeyPress={handleKeyPress}
                placeholder="Type your message..."
                disabled={!isConnected}
              />
              <SendButton 
                type="submit" 
                disabled={!input.trim() || !isConnected}
              >
                <MoveUpRight size={20} />
              </SendButton>
            </InputWrapper>
          </form>
        </InputContainer>
      </ChatContainer>

      {/* Create Offer Modal */}
      <AnimatePresence>
        {showCreateOfferModal && (
          <ModalOverlay
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShowCreateOfferModal(false)}
          >
            <ModalContent
              onClick={e => e.stopPropagation()}
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
            >
              <ModalHeader>
                <h3>Create New Offer</h3>
                <CloseButton onClick={() => {
                  setShowCreateOfferModal(false);
                  setFormErrors({});
                }}>×</CloseButton>
              </ModalHeader>
              <ModalBody>
                <FormGroup>
                  <label>Vehicle</label>
                  <input
                    type="text"
                    value={`${chatVehicle?.year} ${chatVehicle?.make} ${chatVehicle?.model}`}
                    disabled
                  />
                </FormGroup>
                <FormGroup>
                  <label>Offer Amount ($)</label>
                  <input
                    type="number"
                    placeholder="Enter offer amount"
                    value={offerForm.amount}
                    onChange={(e) => {
                      setOfferForm(prev => ({ ...prev, amount: e.target.value }));
                      // Clear error when user starts typing
                      if (formErrors.amount) {
                        setFormErrors(prev => ({ ...prev, amount: '' }));
                      }
                    }}
                    min="0"
                    step="0.01"
                  />
                  {formErrors.amount && (
                    <ErrorMessage>
                      <ErrorIcon>⚠</ErrorIcon>
                      {formErrors.amount}
                    </ErrorMessage>
                  )}
                </FormGroup>
                <FormGroup>
                  <label>Status</label>
                  <select
                    value={offerForm.status}
                    onChange={(e) => setOfferForm(prev => ({ ...prev, status: e.target.value }))}
                  >
                    <option value="pending">Pending</option>
                    <option value="accepted">Accepted</option>
                    <option value="rejected">Rejected</option>
                  </select>
                </FormGroup>
                <ButtonGroup>
                  <SecondaryButton onClick={() => setShowCreateOfferModal(false)}>
                    Cancel
                  </SecondaryButton>
                  <PrimaryButton 
                    onClick={createOffer}
                    disabled={!offerForm.amount || loadingAction}
                  >
                    {loadingAction ? 'Creating...' : 'Create Offer'}
                  </PrimaryButton>
                </ButtonGroup>
              </ModalBody>
            </ModalContent>
          </ModalOverlay>
        )}
      </AnimatePresence>

      {/* Update Offer Modal */}
      <AnimatePresence>
        {showUpdateOfferModal && (
          <ModalOverlay
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShowUpdateOfferModal(false)}
          >
            <ModalContent
              onClick={e => e.stopPropagation()}
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
            >
              <ModalHeader>
                <h3>Update Offer Status</h3>
                <CloseButton onClick={() => setShowUpdateOfferModal(false)}>×</CloseButton>
              </ModalHeader>
              <ModalBody>
                <FormGroup>
                  <label>Active Offer</label>
                  <input
                    type="text"
                    value={activeOffer ? `$${activeOffer.amount} - ${activeOffer.status} ${activeOffer.isAI ? '(AI Offer)' : '(Manual Offer)'}` : 'No active offer'}
                    disabled
                  />
                </FormGroup>
                <FormGroup>
                  <label>New Status</label>
                  <select
                    value={updateOfferForm.status}
                    onChange={(e) => setUpdateOfferForm(prev => ({ ...prev, status: e.target.value }))}
                  >
                    <option value="pending">Pending</option>
                    <option value="accepted">Accepted</option>
                    <option value="rejected">Rejected</option>
                    <option value="countered">Countered</option>
                  </select>
                </FormGroup>
                <ButtonGroup>
                  <SecondaryButton onClick={() => setShowUpdateOfferModal(false)}>
                    Cancel
                  </SecondaryButton>
                  <PrimaryButton 
                    onClick={() => updateOfferStatus(updateOfferForm.status)}
                    disabled={!activeOffer || loadingAction}
                  >
                    {loadingAction ? 'Updating...' : 'Update Status'}
                  </PrimaryButton>
                </ButtonGroup>
              </ModalBody>
            </ModalContent>
          </ModalOverlay>
        )}
      </AnimatePresence>

      {/* Counter Offer Modal */}
      <AnimatePresence>
        {showCounterOfferModal && (
          <ModalOverlay
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShowCounterOfferModal(false)}
          >
            <ModalContent
              onClick={e => e.stopPropagation()}
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
            >
              <ModalHeader>
                <h3>Create Counter Offer</h3>
                <CloseButton onClick={() => {
                  setShowCounterOfferModal(false);
                  setFormErrors({});
                }}>×</CloseButton>
              </ModalHeader>
              <ModalBody>
                <FormGroup>
                  <label>Original Offer</label>
                  <input
                    type="text"
                    value={activeOffer ? `$${activeOffer.amount} - ${activeOffer.status} ${activeOffer.isAI ? '(AI Offer)' : '(Manual Offer)'}` : 'No active offer'}
                    disabled
                  />
                </FormGroup>
                <FormGroup>
                  <label>Counter Offer Amount ($)</label>
                  <input
                    type="number"
                    placeholder="Enter counter offer amount"
                    value={counterOfferForm.amount}
                    onChange={(e) => {
                      setCounterOfferForm(prev => ({ ...prev, amount: e.target.value }));
                      // Clear error when user starts typing
                      if (formErrors.amount) {
                        setFormErrors(prev => ({ ...prev, amount: '' }));
                      }
                    }}
                    min="0"
                    step="0.01"
                  />
                  {formErrors.amount && (
                    <ErrorMessage>
                      <ErrorIcon>⚠</ErrorIcon>
                      {formErrors.amount}
                    </ErrorMessage>
                  )}
                </FormGroup>
                <ButtonGroup>
                  <SecondaryButton onClick={() => setShowCounterOfferModal(false)}>
                    Cancel
                  </SecondaryButton>
                  <PrimaryButton 
                    onClick={createCounterOffer}
                    disabled={!counterOfferForm.amount || !activeOffer || loadingAction}
                  >
                    {loadingAction ? 'Creating...' : 'Create Counter Offer'}
                  </PrimaryButton>
                </ButtonGroup>
              </ModalBody>
            </ModalContent>
          </ModalOverlay>
        )}
      </AnimatePresence>

      {/* Appointment Modal */}
      <AnimatePresence>
        {showAppointmentModal && (
          <ModalOverlay
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShowAppointmentModal(false)}
          >
            <ModalContent
              onClick={e => e.stopPropagation()}
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
            >
              <ModalHeader>
                <h3>Schedule Appointment</h3>
                <CloseButton onClick={() => setShowAppointmentModal(false)}>×</CloseButton>
              </ModalHeader>
              <ModalBody>
                <FormGroup>
                  <label>Appointment Type</label>
                  <select
                    value={appointmentForm.type}
                    onChange={(e) => setAppointmentForm(prev => ({ ...prev, type: e.target.value }))}
                  >
                    <option value="test_drive">Test Drive</option>
                    <option value="consultation">Consultation</option>
                    <option value="purchase">Purchase</option>
                    <option value="service">Service</option>
                  </select>
                </FormGroup>
                <FormGroup>
                  <label>Date</label>
                  <input
                    type="date"
                    value={appointmentForm.date}
                    onChange={(e) => setAppointmentForm(prev => ({ ...prev, date: e.target.value }))}
                    min={new Date().toISOString().split('T')[0]}
                  />
                </FormGroup>
                <FormGroup>
                  <label>Time</label>
                  <input
                    type="time"
                    value={appointmentForm.time}
                    onChange={(e) => setAppointmentForm(prev => ({ ...prev, time: e.target.value }))}
                  />
                </FormGroup>
                <FormGroup>
                  <label>Notes (Optional)</label>
                  <textarea
                    placeholder="Add any additional notes..."
                    value={appointmentForm.notes}
                    onChange={(e) => setAppointmentForm(prev => ({ ...prev, notes: e.target.value }))}
                  />
                </FormGroup>
                <ButtonGroup>
                  <SecondaryButton onClick={() => setShowAppointmentModal(false)}>
                    Cancel
                  </SecondaryButton>
                  <PrimaryButton 
                    onClick={scheduleAppointment}
                    disabled={!appointmentForm.date || !appointmentForm.time || loadingAction}
                  >
                    {loadingAction ? 'Scheduling...' : 'Schedule Appointment'}
                  </PrimaryButton>
                </ButtonGroup>
              </ModalBody>
            </ModalContent>
          </ModalOverlay>
        )}
      </AnimatePresence>
    </>
  );
};

export default DealerChat; 